import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { baseSupabase } from '@/integrations/supabase/client';
import { X, ArrowRight, Info, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

interface BarIlanApplicationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  userTrustScore: number;
  networkSize: number;
}

export default function BarIlanApplicationDialog({
  isOpen,
  onClose,
  onSuccess,
  userId,
  userTrustScore,
  networkSize,
}: BarIlanApplicationDialogProps) {
  const { t } = useTranslation();
  const [motivation, setMotivation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visible, setVisible] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogScrollRef = useRef<HTMLDivElement>(null);
  const settleRafRef = useRef<number | null>(null);
  const settleFramesRef = useRef(0);
  const isIOS = typeof document !== 'undefined' && document.documentElement.classList.contains('platform-ios');
  const iosModalMaxHeight = 'calc(var(--app-height, 100vh) - var(--kbEffective, 0px) - env(safe-area-inset-bottom, 0px) - 1rem)';

  const cancelSettleLoop = () => {
    if (settleRafRef.current !== null) {
      cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = null;
    }
    settleFramesRef.current = 0;
  };

  const startKeyboardSettleLoop = () => {
    cancelSettleLoop();
    if (!isIOS) return;

    const step = () => {
      const container = dialogScrollRef.current;
      const textarea = textareaRef.current;

      if (!container || !textarea || document.activeElement !== textarea) {
        cancelSettleLoop();
        return;
      }

      settleFramesRef.current += 1;

      const containerRect = container.getBoundingClientRect();
      const textareaRect = textarea.getBoundingClientRect();
      const rawKeyboardPadding = getComputedStyle(document.documentElement).getPropertyValue('--kbEffective').trim();
      const keyboardPadding = Number.parseFloat(rawKeyboardPadding);
      const keyboardInset = Number.isFinite(keyboardPadding) ? Math.max(0, keyboardPadding) : 0;
      const safeGap = 24;
      const visibleBottom = containerRect.bottom - safeGap - Math.min(12, keyboardInset * 0.1);
      const textareaBottom = textareaRect.bottom;

      if (textareaBottom > visibleBottom) {
        container.scrollTop += textareaBottom - visibleBottom;
      }

      if (settleFramesRef.current >= 20) {
        cancelSettleLoop();
        return;
      }

      settleRafRef.current = requestAnimationFrame(step);
    };

    settleRafRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    setVisible(false);
    setTimeout(onClose, 200);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isSubmitting]);

  useEffect(() => {
    return () => {
      cancelSettleLoop();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !motivation.trim()) {
      toast.error(t('modals.founding100.toastRequiredFields'));
      return;
    }
    if (motivation.trim().length < 50) {
      toast.error(t('modals.founding100.toastMinChars'));
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { session }, error: sessionError } = await baseSupabase.auth.getSession();
      if (sessionError || !session) {
        toast.error(t('modals.founding100.toastSessionExpired'));
        return;
      }

      const { error } = await baseSupabase
        .from('founding_100_applications')
        .insert([{ user_id: userId, motivation: motivation.trim(), status: 'pending' }])
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error(t('modals.founding100.toastAlreadyApplied'));
        } else {
          toast.error(t('modals.founding100.toastSubmitFailed'));
        }
        return;
      }

      toast.success(t('modals.founding100.toastSuccess'));
      onSuccess();
      handleClose();
      setMotivation('');
    } catch {
      toast.error(t('modals.founding100.toastUnexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const charColor =
    motivation.length === 0 ? 'text-gray-500' :
      motivation.length < 50 ? 'text-yellow-400' :
        motivation.length < 400 ? 'text-emerald-400' :
          'text-pink-400';

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center pointer-events-none p-0 sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="apply-dialog-title"
          className={`w-full sm:max-w-lg pointer-events-auto transition-all duration-200 ${visible
              ? 'opacity-100 translate-y-0 scale-100'
              : 'opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95'
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            ref={dialogScrollRef}
            className="relative rounded-t-2xl sm:rounded-2xl bg-[#0E0E1A] overflow-y-auto max-h-[85vh] sm:max-h-[90vh]"
            style={isIOS
              ? {
                maxHeight: iosModalMaxHeight,
                paddingBottom: 'calc(var(--kbEffective, 0px) + env(safe-area-inset-bottom, 0px) + 1rem)',
              }
              : undefined}
          >
            {/* Gradient border glow */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#E94B8B] via-[#8B5CF6] to-[#E94B8B]" />

            <div className="p-5 sm:p-6">
              {/* Header */}
              <div className="mb-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h2
                      id="apply-dialog-title"
                      className="text-2xl font-bold bg-gradient-to-r from-[#E94B8B] to-[#8B5CF6] bg-clip-text text-transparent"
                    >
                      {t('modals.founding100.title')}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">
                      {t('modals.founding100.subtitle')}
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 hover:rotate-90 transition-all duration-300 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label={t('modals.founding100.closeLabel')}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Urgency badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/25">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                  <span className="text-xs font-semibold text-[#F59E0B]/90">{t('modals.founding100.contestBadge')}</span>
                </div>
              </div>

              {/* Trust Score Hero */}
              <div className="mb-5 group">
                <div className="rounded-xl bg-white/[0.03] border border-white/10 p-5 transition-colors hover:border-white/15">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t('modals.founding100.scoreLabel')}</span>
                      <div className="flex items-baseline gap-3 mt-1">
                        <span className="text-5xl font-extrabold bg-gradient-to-r from-[#E94B8B] to-[#8B5CF6] bg-clip-text text-transparent tabular-nums">
                          {userTrustScore}
                        </span>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-xs font-bold text-emerald-400">{t('modals.founding100.eligibleBadge')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="relative group/tip">
                      <div className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors cursor-help min-h-[44px] min-w-[44px] flex items-center justify-center">
                        <Info className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="absolute end-0 top-full mt-2 w-max max-w-[250px] whitespace-normal p-2.5 rounded-lg bg-[#1a1a2e] border border-white/15 text-xs text-gray-300 opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
                        {t('modals.founding100.eligibilityTooltip')}
                      </div>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('modals.founding100.networkSizeLabel')}</span>
                    <span className="text-base font-semibold text-white">{networkSize} {t('modals.founding100.connectionsLabel')}</span>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                {/* Motivation */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="motivation-field" className="text-sm font-semibold text-white">
                      {t('modals.founding100.questionLabel')}
                    </label>
                    <span className={`text-xs font-mono transition-colors ${charColor}`}>
                      {motivation.length}/500
                    </span>
                  </div>

                  <textarea
                    ref={textareaRef}
                    id="motivation-field"
                    value={motivation}
                    onChange={(e) => setMotivation(e.target.value)}
                    onFocus={startKeyboardSettleLoop}
                    onBlur={cancelSettleLoop}
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full rounded-xl bg-white/[0.03] border border-white/15 px-4 py-3 text-[16px] sm:text-sm text-white placeholder-gray-600 focus:border-[#E94B8B]/60 focus:outline-none focus:ring-2 focus:ring-[#E94B8B]/20 resize-none transition-all"
                    style={isIOS ? { scrollMarginBottom: 'calc(var(--kbEffective, 0px) + 5rem)' } : undefined}
                    placeholder={t('modals.founding100.placeholder')}
                  />

                  <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                    {t('modals.founding100.tip')}
                  </p>

                  {motivation.length > 0 && motivation.length < 50 && (
                    <p className="mt-2 text-xs text-yellow-400/80 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      {t('modals.founding100.minCharsHint')}
                    </p>
                  )}
                </div>

                {/* Commitment notice */}
                <div className="mb-5 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/15 p-3">
                  <div className="flex gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-[#F59E0B]/70 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#F59E0B]/70 leading-relaxed">
                      {t('modals.founding100.warning')}
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting || !motivation.trim() || motivation.trim().length < 50}
                    className="relative flex-1 group overflow-hidden rounded-xl px-6 py-3.5 font-bold text-white transition-all disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 min-h-[48px]"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#E94B8B] to-[#8B5CF6] transition-opacity" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#E94B8B] to-[#8B5CF6] opacity-0 group-hover:opacity-100 group-disabled:opacity-0 blur-xl transition-opacity" />
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full group-disabled:translate-x-[-100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                    <span className="relative flex items-center justify-center gap-2">
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('modals.founding100.submitting')}
                        </>
                      ) : (
                        <>
                          {t('modals.founding100.submit')}
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="rounded-xl bg-white/5 border border-white/15 px-5 py-3.5 font-semibold text-gray-300 transition-all hover:bg-white/10 hover:text-white active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 min-h-[48px]"
                  >
                    {t('modals.founding100.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
